import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { GUARDRAILS } from "@/constants/testIds";
import { toast } from "sonner";
import { Save, X, Shield, Plus } from "lucide-react";

// v28.1 — Human-friendly seconds label: 45s / 2m 30s / 30m
const formatWait = (s) => {
  const n = parseInt(s, 10);
  if (!n && n !== 0) return "—";
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const r = n % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
};

function ChipsInput({ value, onChange, placeholder, testId }) {
  const [text, setText] = useState("");
  const add = () => {
    const t = text.trim();
    if (!t) return;
    if (!value.includes(t)) onChange([...value, t]);
    setText("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 border border-slate-200 px-2 py-1 rounded font-semibold">
            {v}
            <button onClick={() => onChange(value.filter((x) => x !== v))} className="text-slate-500 hover:text-red-600">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          data-testid={testId}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
        />
        <button onClick={add} className="inline-flex items-center gap-1 border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-2 rounded-md hover:bg-slate-50 transition-colors duration-150">
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
    </div>
  );
}

export default function Guardrails() {
  const [g, setG] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => setG((await api.get("/guardrails")).data);
  useEffect(() => { load(); }, []);

  if (!g) return <div className="p-10 text-sm text-slate-500">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/guardrails", {
        company_policies: g.company_policies,
        forbidden_phrases: g.forbidden_phrases,
        escalation_triggers: g.escalation_triggers,
        ai_liberty: g.ai_liberty,
        live_mode_when_to_enter: g.live_mode_when_to_enter,
        live_mode_offer_help_message: g.live_mode_offer_help_message,
        live_mode_followup_ask_message: g.live_mode_followup_ask_message,
        live_mode_wait_seconds: g.live_mode_wait_seconds,
        live_mode_smart_wait: !!g.live_mode_smart_wait,
      });
      toast.success("Guardrails saved");
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={GUARDRAILS.root} className="p-8 lg:p-12 max-w-[900px]">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">AI Governance</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <Shield className="h-7 w-7 text-orange-600" /> AI Guardrails
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Keep the AI on-brand and inside company policy at all times.
          </p>
        </div>
        <button
          data-testid={GUARDRAILS.saveBtn}
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-orange-600 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-orange-500 transition-colors duration-150 disabled:opacity-60"
        >
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="space-y-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 block mb-2">
            Company policies (system prompt injection)
          </label>
          <textarea
            data-testid={GUARDRAILS.policiesTextarea}
            rows={5}
            value={g.company_policies}
            onChange={(e) => setG({ ...g, company_policies: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150 font-mono"
          />
          <p className="text-xs text-slate-500 mt-2">
            This is injected into every AI system prompt. Be specific about tone, promises, and no-goes.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 block mb-2">
            Forbidden phrases
          </label>
          <ChipsInput
            testId={GUARDRAILS.forbiddenInput}
            value={g.forbidden_phrases}
            onChange={(v) => setG({ ...g, forbidden_phrases: v })}
            placeholder="e.g. guaranteed pass"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 block mb-2">
            Escalation triggers (words that pause AI and flag human)
          </label>
          <ChipsInput
            testId={GUARDRAILS.escalationInput}
            value={g.escalation_triggers}
            onChange={(v) => setG({ ...g, escalation_triggers: v })}
            placeholder="e.g. refund, complaint"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              AI Liberty · {g.ai_liberty}/100
            </label>
            <div className="text-xs text-slate-600">
              {g.ai_liberty <= 30 ? "Strict: KB-only" : g.ai_liberty <= 70 ? "Balanced: KB + paraphrase" : "Loose: free reasoning"}
            </div>
          </div>
          <div className="liberty-track h-2 rounded relative">
            <input
              data-testid={GUARDRAILS.libertySlider}
              type="range"
              min="0" max="100"
              value={g.ai_liberty}
              onChange={(e) => setG({ ...g, ai_liberty: parseInt(e.target.value, 10) })}
              className="absolute inset-0 w-full opacity-0 cursor-pointer"
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 h-4 w-4 bg-white border-2 border-slate-900 rounded-full shadow-md pointer-events-none"
              style={{ left: `calc(${g.ai_liberty}% - 8px)` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-slate-500 mt-2 font-semibold uppercase tracking-widest">
            <span>KB only</span>
            <span>Balanced</span>
            <span>Free reasoning</span>
          </div>
        </div>

        {/* ---- Live Sales Mode ---- */}
        <div
          className="bg-orange-50/60 border border-orange-200 rounded-lg p-6"
          data-testid="live-mode-section"
        >
          <div className="mb-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-orange-700 mb-1">
              Live Sales Mode
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              When a user shows subscription intent, Pippy enters Live Sales Mode: offers help first,
              waits N seconds, and if silent — asks for a follow-up time. All 3 knobs below are read
              live by the AI classifier — no restart needed.
            </p>
          </div>

          <div className="mb-4">
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 block mb-2">
              When to enter (rule the classifier reads)
            </label>
            <textarea
              data-testid="live-mode-when-to-enter"
              rows={4}
              value={g.live_mode_when_to_enter || ""}
              onChange={(e) => setG({ ...g, live_mode_when_to_enter: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
              placeholder="Plain English rule — the LLM classifier reads this to decide when to enter Live Sales Mode."
            />
            <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
              Example: <em>Enter when user asks about subscribing/pricing/checkout, OR when CPS jumps 15+ in this conversation. Never enter if user said &quot;later&quot;.</em>
            </p>
          </div>

          <div className="mb-4">
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 block mb-2">
              Message 1 — Intent guidance (sent immediately on entry)
            </label>
            <textarea
              data-testid="live-mode-offer-help"
              rows={3}
              value={g.live_mode_offer_help_message || ""}
              onChange={(e) => setG({ ...g, live_mode_offer_help_message: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
              placeholder="Describe what the AI should convey — e.g. 'offer help completing the checkout, reference any weak areas they mentioned earlier'..."
            />
            <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
              <b>Not a template.</b> AI drafts the actual message using conversation context; your text tells it <em>what to convey</em>. Use{" "}
              <code className="bg-slate-100 px-1 rounded text-[10px]">{"{name}"}</code> if you want the user&apos;s first name referenced.
            </p>
          </div>

          <div className="mb-4">
            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 block mb-2">
              Message 2 — Follow-up ask guidance (sent after wait, if no reply)
            </label>
            <textarea
              data-testid="live-mode-followup-ask"
              rows={3}
              value={g.live_mode_followup_ask_message || ""}
              onChange={(e) => setG({ ...g, live_mode_followup_ask_message: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
              placeholder="Describe what the AI should convey — e.g. 'reassure no pressure, ask for a good time to follow up, encourage them to say something like after 7pm or tomorrow'..."
            />
            <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
              <b>Not a template.</b> AI drafts the actual message using conversation context; your text tells it <em>what to convey</em>.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                {g.live_mode_smart_wait
                  ? `Max wait (AI picks per user) · ceiling ${formatWait(g.live_mode_wait_seconds ?? 60)}`
                  : `Wait before Message 2 · ${formatWait(g.live_mode_wait_seconds ?? 60)}`}
              </label>
              <label
                className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest cursor-pointer"
                title="When ON, AI picks the wait per user from their CPS (hot leads get the short end, cold leads get the ceiling). The slider becomes the maximum."
              >
                <input
                  data-testid="live-mode-smart-wait-toggle"
                  type="checkbox"
                  checked={!!g.live_mode_smart_wait}
                  onChange={(e) => setG({ ...g, live_mode_smart_wait: e.target.checked })}
                  className="accent-orange-600"
                />
                <span className={g.live_mode_smart_wait ? "text-orange-700" : "text-slate-500"}>
                  Smart wait {g.live_mode_smart_wait ? "ON" : "OFF"}
                </span>
              </label>
            </div>
            <input
              data-testid="live-mode-wait-seconds"
              type="range"
              min="15" max="1800" step="15"
              value={g.live_mode_wait_seconds ?? 60}
              onChange={(e) => setG({ ...g, live_mode_wait_seconds: parseInt(e.target.value, 10) })}
              className="w-full accent-orange-600"
            />
            <div className="flex justify-between text-[10px] text-slate-500 mt-1 font-semibold uppercase tracking-widest">
              <span>15s (aggressive)</span>
              <span>60s (default)</span>
              <span>30m (patient)</span>
            </div>
            {g.live_mode_smart_wait && (
              <div className="mt-2 bg-orange-50 border border-orange-200 rounded p-2 text-[11px] text-orange-900 leading-relaxed">
                <b>Smart wait is ON.</b> Each user gets their own wait time:
                hot leads (CPS 100) get <b>15s</b>, cold leads (CPS 0) get up to
                the ceiling of <b>{formatWait(g.live_mode_wait_seconds ?? 60)}</b>.
                Everyone else lands linearly in between — so a CPS 60 user
                waits {formatWait(Math.round(15 + (100 - 60) * ((g.live_mode_wait_seconds ?? 60) - 15) / 100))}.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
